import React, { useContext, useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { notifyError, notifySuccess } from "../../actions/notifications";
import SwaggerContext from "../../SwaggerContext";
import { updateAuthUser, logout } from "../../actions/auth";
import { createApiCallback } from "../../utils/error-handler";
import EmailValidationBanner from "../../components/EmailValidationBanner";
import ConfirmationModal from "../../components/ConfirmationModal";

const initialState = {
  pagination: {
    page: 0,
    limit: 100,
  },
};

const Profile = () => {
  const { userApi, roleApi } = useContext(SwaggerContext);
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [lastClick, setLastClick] = useState(0);
  const [originalEmail, setOriginalEmail] = useState("");
  const [emailChanged, setEmailChanged] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  const loadUser = () => {
    userApi.getUserById(user.id, createApiCallback(
      (userData) => {
        userData.password = "";
        userData.confirmPassword = "";
        setData(userData);
        setOriginalEmail(userData.email || "");
        setEmailChanged(false);
      },
      (errorMessage, errorInfo) => {
        if (errorInfo.statusCode === 404) {
          notifyError("No se ha encontrado el usuario");
        } else {
          notifyError(`Ha habido un error al cargar el usuario: ${errorMessage}`);
        }
        navigate("/");
      }
    ));
  };

  const loadRoles = (opts) => {
    if (user.role === "ADMIN") {
      roleApi.getRoles(opts, createApiCallback(
        (rolesData) => {
          setRoles(rolesData.roles);
        },
        (errorMessage) => {
          notifyError(`Error al cargar los roles: ${errorMessage}`);
        }
      ));
    } else {
      setRoles([]);
    }
  };

  useEffect(() => {
    let opts = initialState.pagination;
    loadRoles(opts);
    loadUser();
  }, []);

  useEffect(() => {
    if (user.validated === false && data !== null) {
      const dismissed = sessionStorage.getItem('email-validation-dismissed');
      if (!dismissed) {
        setShowValidationModal(true);
      }
    } else if (user.validated === true) {
      sessionStorage.removeItem('email-validation-dismissed');
      setShowValidationModal(false);
    }
  }, [user.validated, data]);

  const handleChange = (field, value) => {
    if (field === "email") {
      setEmailChanged(value !== originalEmail);
    }
    setData((previousData) => ({ ...previousData, [field]: value }));
  };

  const validateForm = () => {
    let errs = {};
    const regex = new RegExp("^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*.])");
    if (!data.name) errs.name = true;
    if (!data.username) errs.username = true;
    if (!data.surname) errs.surname = true;
    if (!data.email) errs.email = true;
    if (user.role === "ADMIN" && !data.role_id) errs.role_id = true;
    if (data.password && !regex.test(data.password)) errs.password = true;
    if (data.password && data.password !== data.confirmPassword) errs.confirmPassword = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      notifyError("Falta alguno de los campos obligatorios.");
      return;
    }

    setLoading(true);

    try {
      const userDto = {
        id: data.id,
        name: data.name,
        surname: data.surname,
        username: data.username,
        email: data.email,
        ...(user.role === "ADMIN" ? { role_id: data.role_id } : {}),
        ...(data.password ? { password: data.password } : {}),
        ...(emailChanged ? { validated: false } : {}),
      };

      await new Promise((resolve, reject) => {
        userApi.updateUserById(data.id, userDto, createApiCallback(
          (payload) => {
            notifySuccess("Perfil actualizado correctamente.");
            dispatch(updateAuthUser(payload));
            setData((prev) => ({ ...prev, password: "", confirmPassword: "" }));
            if (emailChanged) {
              setOriginalEmail(userDto.email);
              handleResend();
            }
            resolve();
          },
          (errorMessage) => {
            notifyError(`Ha habido un error al actualizar el perfil: ${errorMessage}`);
            reject(new Error("updateUserById"));
          }
        ));
      });
    } catch (error) {
      // Error already notified
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const now = Date.now();
    if (now - lastClick < 60000) {
      notifyError("Has pulsado demasiado rápido. Espera 60 segundos antes de volver a intentarlo.");
      return Promise.resolve();
    }
    setLastClick(now);

    const token = user?.validationToken || user?.verificationToken || user?.token;
    if (!token) {
      notifyError("No se ha encontrado el token de validación en tu sesión. Vuelve a iniciar sesión e inténtalo de nuevo.");
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      userApi.resendValidationMail(token, (error) => {
        if (error) notifyError("Error al reenviar el email.");
        else notifySuccess("Email reenviado. Revisa tu bandeja de entrada.");
        resolve();
      });
    });
  };

  const handleDeleteAccount = () => {
    setLoading(true);
    userApi.deleteUserById(user.id, createApiCallback(
      () => {
        notifySuccess("Tu cuenta ha sido eliminada correctamente");
        dispatch(logout(userApi));
        navigate("/");
      },
      (errorMessage) => {
        notifyError(`Error al eliminar la cuenta: ${errorMessage}`);
        setLoading(false);
      },
      204
    ));
  };

  if (data === null) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "calc(100vh - 200px)" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <EmailValidationBanner
        visible={user.validated === false && showValidationModal}
        email={data.email}
        onClose={() => {
          setShowValidationModal(false);
          sessionStorage.setItem('email-validation-dismissed', 'true');
        }}
        onResend={handleResend}
      />

      <div className="text-center mb-5">
        <h2>Mi Perfil</h2>
      </div>

      <Form onSubmit={handleSave} style={{ maxWidth: "600px", margin: "0 auto" }}>
        {/* Nombre */}
        <Form.Group controlId="name" className="mb-4">
          <Form.Label>Nombre</Form.Label>
          <Form.Control
            isInvalid={errors.name}
            type="text"
            placeholder="Nombre"
            value={data.name}
            onChange={(e) => handleChange('name', e.target.value)}
            disabled={loading}
          />
        </Form.Group>

        {/* Apellido */}
        <Form.Group controlId="surname" className="mb-4">
          <Form.Label>Apellido</Form.Label>
          <Form.Control
            isInvalid={errors.surname}
            type="text"
            placeholder="Apellido"
            value={data.surname}
            onChange={(e) => handleChange('surname', e.target.value)}
            disabled={loading}
          />
        </Form.Group>

        {/* Email */}
        <Form.Group controlId="email" className="mb-4">
          <Form.Label>Email</Form.Label>
          <Form.Control
            isInvalid={errors.email}
            type="email"
            placeholder="Email"
            value={data.email}
            onChange={(e) => handleChange('email', e.target.value)}
            disabled={loading}
          />
          {!user?.validated && (
            <Form.Text className="text-warning d-block mt-2">
              Email no validado
            </Form.Text>
          )}
          {emailChanged && (
            <Form.Text className="text-info d-block mt-2">
              Al cambiar el email deberás validarlo nuevamente
            </Form.Text>
          )}
        </Form.Group>

        {/* Usuario */}
        <Form.Group controlId="username" className="mb-4">
          <Form.Label>Nombre de Usuario</Form.Label>
          <Form.Control
            isInvalid={errors.username}
            type="text"
            placeholder="Usuario"
            value={data.username}
            onChange={(e) => handleChange('username', e.target.value)}
            disabled={loading}
          />
        </Form.Group>

        {/* Rol (solo para admins) */}
        {user.role === "ADMIN" && (
          <Form.Group controlId="role_id" className="mb-4">
            <Form.Label>Rol</Form.Label>
            <Form.Select
              isInvalid={errors.role_id}
              value={data.role_id || ""}
              onChange={(e) => handleChange('role_id', e.target.value)}
              disabled={loading}
            >
              <option value="">Seleccionar rol</option>
              {roles
                ? roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))
                : null}
            </Form.Select>
          </Form.Group>
        )}

        {/* Divider */}
        <hr className="my-5" />

        {/* Cambiar Contraseña */}
        <h5 className="mb-4">Cambiar Contraseña (Opcional)</h5>

        {/* Nueva Contraseña */}
        <Form.Group controlId="password" className="mb-4">
          <Form.Label>Nueva Contraseña</Form.Label>
          <Form.Control
            isInvalid={errors.password}
            type="password"
            placeholder="Dejar vacío para no cambiar"
            value={data.password}
            onChange={(e) => handleChange('password', e.target.value)}
            disabled={loading}
          />
          <Form.Control.Feedback type="invalid">
            La contraseña debe tener al menos 8 caracteres y contiene al menos
            una letra mayúscula, una letra minúscula, un número y uno de estos
            símbolos(!@#$%^&*.).
          </Form.Control.Feedback>
        </Form.Group>

        {/* Confirmar Contraseña */}
        <Form.Group controlId="confirmPassword" className="mb-5">
          <Form.Label>Confirmar Contraseña</Form.Label>
          <Form.Control
            isInvalid={errors.confirmPassword}
            type="password"
            placeholder="Confirmar contraseña"
            value={data.confirmPassword}
            onChange={(e) => handleChange('confirmPassword', e.target.value)}
            disabled={loading}
          />
          <Form.Control.Feedback type="invalid">
            Las contraseñas deben coincidir
          </Form.Control.Feedback>
        </Form.Group>

        {/* Botón Guardar */}
        <div className="d-grid gap-2 mb-3">
          <button
            type="submit"
            className="btn btn-primary btn-lg"
            disabled={loading}
          >
            {loading ? "Guardando..." : "Guardar Cambios"}
          </button>
        </div>

        {/* Botón Logout */}
        <div className="d-grid gap-2 mb-3">
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => {
              if (window.confirm("¿Estás seguro que deseas cerrar sesión?")) {
                dispatch(logout(userApi));
              }
            }}
            disabled={loading}
          >
            Cerrar Sesión
          </button>
        </div>

        {/* Divider para sección peligrosa */}
        <hr className="my-4" style={{ borderColor: "#dc3545" }} />

        {/* Zona de peligro */}
        <div className="text-center mb-3">
          <h6 className="text-danger">Zona de Peligro</h6>
          <p className="text-muted small">Esta acción no se puede deshacer</p>
        </div>

        {/* Botón Eliminar Cuenta */}
        <div className="d-grid gap-2 mb-5">
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={loading}
          >
            Eliminar mi Cuenta
          </button>
        </div>
      </Form>

      {/* Modal de confirmación de eliminación */}
      <ConfirmationModal
        show={showDeleteModal}
        setShowModal={setShowDeleteModal}
        deleteFunction={handleDeleteAccount}
        deleteArguments={[]}
        title="¿Estás seguro que deseas eliminar tu cuenta?"
        message="Esta acción eliminará permanentemente tu cuenta y todos tus datos. No podrás recuperarlos."
      />

      {/* Banner de validación inferior */}
      {user.validated === false && (
        <div 
          className="d-flex align-items-center mt-5 p-3 rounded"
          style={{
            backgroundColor: "#fff3cd",
            borderLeft: "4px solid #f39c12",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          <div style={{ flex: 1 }}>
            <p style={{ fontWeight: "bold", color: "#856404", marginBottom: "4px" }}>
              Email no validado
            </p>
            <p style={{ fontSize: "12px", color: "#856404", margin: 0 }}>
              Por favor, valida tu email para acceder a todas las funcionalidades
            </p>
          </div>
          <button
            type="button"
            className="btn btn-warning btn-sm"
            style={{ marginLeft: "12px", whiteSpace: "nowrap" }}
            onClick={() => setShowValidationModal(true)}
            disabled={loading}
          >
            Validar
          </button>
        </div>
      )}
    </>
  );
};

export default Profile;
